import React, { useState, useEffect } from 'react';
import { ExternalLink, AlertCircle, Loader, Copy, Check } from 'lucide-react';
import api from '../utils/axios';
import { Text, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';

interface ApplicationUrlsModalProps {
  projectId: string;
  onClose: () => void;
}

interface ProjectUrls {
  id: string;
  project_id: string;
  url_lines: string[]; // Raw URL lines as received
  last_updated_from_thread_id?: string;
  last_updated_from_message_id?: string;
  created_at: number;
  updated_at: number;
  user_id: string;
}

// Link icon SVG component
const LinkIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg 
    className={className}
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M9 17H7C4.23858 17 2 14.7614 2 12C2 9.23858 4.23858 7 7 7H9M15 7H17C19.7614 7 22 9.23858 22 12C22 14.7614 19.7614 17 17 17H15M7 12L17 12" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

// External Link icon SVG component
const ExternalLinkIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg 
    className={className}
    viewBox="0 0 24 24" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M7 17L17 7M17 7H7M17 7V17" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

// Skeleton Loading Component for URL Cards
const UrlCardSkeleton: React.FC<{ index: number }> = ({ index }) => {
  return (
    <div 
      className="animate-fade-in-up opacity-0"
      style={{ 
        animationDelay: `${index * 0.08}s`,
        animationFillMode: 'forwards'
      }}
    >
      <div 
        className="bg-bg-primary border border-border-secondary box-border flex gap-[8px] items-start p-[var(--spacing-lg,12px)] rounded-[var(--radius-md,8px)] w-full"
        style={{
          boxShadow: "0px 1px 3px 0px var(--shadow-sm-01, rgba(10, 13, 18, 0.1)), 0px 1px 2px -1px var(--shadow-sm-02, rgba(10, 13, 18, 0.1))"
        }}
      >
        {/* Icon Skeleton */}
        <div 
          className="skeleton-shimmer rounded-[17px] shrink-0"
          style={{
            width: '34px',
            height: '34px',
          }}
        />

        {/* Content Skeleton */}
        <div className="flex flex-col gap-[6px] items-start flex-1 min-w-0">
          {/* Label Skeleton */}
          <div className="h-5 skeleton-shimmer rounded w-2/5"></div>
          
          {/* URL Skeleton */}
          <div className="h-6 skeleton-shimmer rounded w-4/5"></div>
        </div>

        {/* Copy Button Skeleton */}
        <div className="w-[28px] h-[28px] skeleton-shimmer rounded shrink-0"></div>
      </div>
    </div>
  );
};

export const ApplicationUrlsModal: React.FC<ApplicationUrlsModalProps> = ({ 
  projectId,
  onClose,
}) => {
  const [urls, setUrls] = useState<ProjectUrls | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);

  useEffect(() => {
    fetchApplicationUrls();
  }, [projectId]);

  const fetchApplicationUrls = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/projects/${projectId}/urls`);
      setUrls(response.data);
    } catch (error: any) {
      console.error('Failed to fetch application URLs:', error);
      if (error.response?.status === 404) {
        // No URLs found - this is not an error, just empty state
        setUrls(null);
      } else {
        setError('Failed to load application URLs');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handleUrlClick = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleCopyUrl = async (url: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent the card click from opening the URL
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedUrl(null);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  const extractUrlLabel = (urlLine: string): string => {
    // Extract label before the colon
    const colonIndex = urlLine.indexOf(':');
    if (colonIndex === -1) return urlLine;
    return urlLine.substring(0, colonIndex).trim();
  };

  const extractUrlFromLine = (urlLine: string): string | null => {
    // Extract URL after the colon
    const colonIndex = urlLine.indexOf(':');
    if (colonIndex === -1) return null;
    
    const url = urlLine.substring(colonIndex + 1).trim();
    return url.startsWith('http') ? url : null;
  };

  const renderUrlItem = (urlLine: string, index: number) => {
    const url = extractUrlFromLine(urlLine);
    const label = extractUrlLabel(urlLine);
    const isCopied = copiedUrl === url;
    
    if (!url) return null;

    return (
      <div
        key={index}
        onClick={() => handleUrlClick(url)}
        className="bg-bg-primary border border-border-secondary box-border flex gap-[8px] items-start p-[var(--spacing-lg,12px)] rounded-[var(--radius-md,8px)] w-full group relative cursor-pointer hover:bg-bg-secondary-hover transition-colors animate-fade-in-up opacity-0"
        style={{
          boxShadow: "0px 1px 3px 0px var(--shadow-sm-01, rgba(10, 13, 18, 0.1)), 0px 1px 2px -1px var(--shadow-sm-02, rgba(10, 13, 18, 0.1))",
          animationDelay: `${index * 0.08}s`,
          animationFillMode: 'forwards'
        }}
      >
        {/* Icon */}
        <div 
          className="bg-bg-tertiary box-border flex gap-[8px] items-center justify-center px-[8px] py-[7px] rounded-[17px] shrink-0"
          style={{
            width: '34px',
            height: '34px',
            boxShadow: "0px 1px 3px 0px var(--shadow-sm-01, rgba(10, 13, 18, 0.1)), 0px 1px 2px -1px var(--shadow-sm-02, rgba(10, 13, 18, 0.1))"
          }}
        >
          <LinkIcon className="w-[16px] h-[16px] text-fg-primary" />
        </div>

        {/* Content */}
        <div className="flex flex-col gap-[2px] items-start flex-1 min-w-0">
          {/* Label */}
          <Text 
            variant="text-sm" 
            weight="medium" 
            color="primary"
            className="whitespace-nowrap overflow-hidden text-ellipsis w-full"
          >
            {label}
          </Text>
          
          {/* URL with Arrow Icon */}
          <div className="flex gap-[var(--spacing-xs,4px)] items-center w-full min-w-0">
            <Text 
              variant="text-md" 
              weight="medium" 
              color="secondary"
              className="whitespace-nowrap overflow-hidden text-ellipsis group-hover:text-text-primary transition-colors"
            >
              {url}
            </Text>
            <ExternalLinkIcon className="w-[16px] h-[16px] text-text-secondary group-hover:text-text-primary transition-colors shrink-0" />
          </div>
        </div>

        {/* Copy Button */}
        <button
          onClick={(e) => handleCopyUrl(url, e)}
          className="p-[6px] rounded-[var(--radius-sm,6px)] hover:bg-bg-secondary transition-colors shrink-0 z-10"
          title={isCopied ? "Copied!" : "Copy URL"}
        >
          {isCopied ? (
            <Check className="w-[16px] h-[16px] text-text-success-primary" />
          ) : (
            <Copy className="w-[16px] h-[16px] text-text-tertiary hover:text-text-secondary transition-colors" />
          )}
        </button>
      </div>
    );
  };

  return (
    <div 
      className={`fixed inset-0 bg-alpha-black-70 z-50 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`} 
      onClick={handleClose}
    >
      <div
        className={`absolute right-0 top-0 h-full bg-bg-primary w-[400px] flex flex-col border-l border-border-secondary-alt ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: "0 20px 24px -4px rgba(10, 13, 18, 0.08), 0 8px 8px -4px rgba(10, 13, 18, 0.03), 0 3px 3px -1.5px rgba(10, 13, 18, 0.04)"
        }}
      >
        {/* Header */}
        <div className="bg-bg-primary box-border flex gap-[var(--spacing-md,8px)] items-start p-[var(--spacing-3xl,24px)] w-full">
          <div className="flex flex-[1_0_0] gap-[var(--spacing-xl,16px)] items-start min-h-px min-w-px">
            <div className="flex flex-[1_0_0] flex-col gap-[var(--spacing-xxs,2px)] items-start min-h-px min-w-px whitespace-pre-wrap">
              <Text variant="text-lg" weight="semibold" color="primary" className="w-full">
                Application URLs
              </Text>
              <Text variant="text-sm" weight="regular" color="secondary" className="w-full">
                Access your deployed application endpoints
              </Text>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="flex items-center justify-center rounded-full w-8 h-8 text-fg-secondary hover:bg-bg-primary-hover transition-all duration-200"
          >
            <Icon name={IconName.Cross} size="md" inheritColor />
          </button>
        </div>

        {/* Divider */}
        <div className="box-border flex flex-col gap-[16px] items-center px-[var(--spacing-3xl,24px)] py-0 w-full">
          <div className="bg-border-secondary h-px w-full" />
        </div>

        {/* Content */}
        <div className="box-border flex flex-col gap-[16px] items-center px-[var(--spacing-3xl,24px)] py-[var(--spacing-3xl,24px)] w-full flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col gap-[var(--spacing-lg,12px)] w-full">
              {[0, 1, 2].map((index) => (
                <UrlCardSkeleton key={index} index={index} />
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-[var(--spacing-8xl,80px)] gap-[var(--spacing-lg,12px)]">
              <AlertCircle className="w-12 h-12 text-text-error-primary" />
              <Text variant="text-md" weight="semibold" color="primary">Error Loading URLs</Text>
              <Text variant="text-sm" color="secondary" align="center">{error}</Text>
              <button
                onClick={fetchApplicationUrls}
                className="mt-[var(--spacing-md,8px)] px-[var(--spacing-xl,16px)] py-[var(--spacing-md,8px)] bg-bg-brand-solid text-text-primary-on-brand rounded-[var(--radius-md,8px)] hover:bg-bg-brand-solid-hover transition-colors"
              >
                <Text variant="text-sm" weight="medium" color="on-brand">Try Again</Text>
              </button>
            </div>
          ) : !urls || !urls.url_lines || urls.url_lines.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-[var(--spacing-8xl,80px)] gap-[var(--spacing-lg,12px)] text-center w-full">
              <LinkIcon className="w-12 h-12 text-text-quaternary" />
              <Text variant="text-md" weight="semibold" color="primary" align="center">No Application URLs Found</Text>
              <Text variant="text-sm" color="secondary" align="center" className="max-w-[280px]">
                Application URLs will appear here once your project is deployed by the development assistant.
              </Text>
            </div>
          ) : (
            <div className="flex flex-col gap-[var(--spacing-lg,12px)] w-full py-[var(--spacing-md,8px)]">
              {urls.url_lines.map((urlLine, index) => 
                renderUrlItem(urlLine, index)
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplicationUrlsModal;
